export type MapViewMode = '2d' | '3d';

export const MAP_VIEW_MODE_STORAGE_KEY = 'pratus.mapViewMode';

/** High enough that a global SceneView reads as a round Earth. */
export const GLOBE_ALTITUDE_M = 18_000_000;

export type SyncStorage = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
};

export type MapViewpoint = {
  longitude: number;
  latitude: number;
  zoom?: number;
  altitudeM?: number;
};

function defaultStorage(): SyncStorage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

export function readMapViewMode(
  storage: SyncStorage | null = defaultStorage()
): MapViewMode {
  try {
    return storage?.getItem(MAP_VIEW_MODE_STORAGE_KEY) === '3d' ? '3d' : '2d';
  } catch {
    return '2d';
  }
}

export function writeMapViewMode(
  mode: MapViewMode,
  storage: SyncStorage | null = defaultStorage()
): void {
  try {
    storage?.setItem(MAP_VIEW_MODE_STORAGE_KEY, mode);
  } catch {
    // Private mode or blocked storage should not break the toggle.
  }
}

/** Approximate camera height for a Web Mercator zoom (480px half-viewport). */
export function zoomToAltitudeMeters(zoom: number, latitude = 0): number {
  const earthCircumference = 40075016.686;
  const latRad = (latitude * Math.PI) / 180;
  const metersPerPixel =
    (earthCircumference * Math.cos(latRad)) / (256 * 2 ** zoom);
  return Math.max(200, metersPerPixel * 480);
}

export function altitudeMetersToZoom(altitudeM: number, latitude = 0): number {
  const earthCircumference = 40075016.686;
  const latRad = (latitude * Math.PI) / 180;
  const metersPerPixel = Math.max(1, altitudeM / 480);
  const zoom = Math.log2(
    (earthCircumference * Math.cos(latRad)) / (256 * metersPerPixel)
  );
  if (!Number.isFinite(zoom)) return 4;
  return Math.min(20, Math.max(0, zoom));
}

function lonLat(
  point: unknown
): { longitude: number; latitude: number; z?: number } | null {
  if (!point || typeof point !== 'object') return null;
  const candidate = point as {
    longitude?: unknown;
    latitude?: unknown;
    z?: unknown;
  };
  if (
    typeof candidate.longitude !== 'number' ||
    typeof candidate.latitude !== 'number'
  ) {
    return null;
  }
  return {
    longitude: candidate.longitude,
    latitude: candidate.latitude,
    ...(typeof candidate.z === 'number' ? { z: candidate.z } : {}),
  };
}

/** Snapshot a live MapView or SceneView without depending on ArcGIS class types. */
export function snapshotArcgisViewpoint(view: {
  type?: string;
  center?: unknown;
  zoom?: number;
  camera?: { position?: unknown } | null;
}): MapViewpoint | null {
  if (view.type === '3d') {
    const position = lonLat(view.camera?.position);
    if (position) {
      return snapshotViewpoint({
        zoom: view.zoom,
        camera: { position },
      });
    }
  }
  const center = lonLat(view.center);
  if (!center) return null;
  return snapshotViewpoint({
    center,
    zoom: view.zoom,
  });
}

export function snapshotViewpoint(view: {
  center?: { longitude?: number; latitude?: number } | null;
  zoom?: number;
  camera?: {
    position?: { longitude?: number; latitude?: number; z?: number };
  } | null;
}): MapViewpoint | null {
  const cam = view.camera?.position;
  if (
    cam &&
    typeof cam.longitude === 'number' &&
    typeof cam.latitude === 'number'
  ) {
    return {
      longitude: cam.longitude,
      latitude: cam.latitude,
      altitudeM: typeof cam.z === 'number' ? cam.z : undefined,
      zoom: typeof view.zoom === 'number' ? view.zoom : undefined,
    };
  }
  const center = view.center;
  if (
    center &&
    typeof center.longitude === 'number' &&
    typeof center.latitude === 'number'
  ) {
    return {
      longitude: center.longitude,
      latitude: center.latitude,
      zoom: typeof view.zoom === 'number' ? view.zoom : undefined,
    };
  }
  return null;
}

export function sceneCameraFromViewpoint(
  viewpoint: MapViewpoint | null,
  fallback: { center: [number, number]; zoom: number }
): {
  position: { longitude: number; latitude: number; z: number };
  tilt: number;
  heading: number;
} {
  const longitude = viewpoint?.longitude ?? fallback.center[0];
  const latitude = viewpoint?.latitude ?? fallback.center[1];
  const z =
    viewpoint?.altitudeM ??
    (viewpoint?.zoom != null
      ? zoomToAltitudeMeters(viewpoint.zoom, latitude)
      : GLOBE_ALTITUDE_M);
  return {
    position: { longitude, latitude, z },
    tilt: 0,
    heading: 0,
  };
}

export function mapCenterZoomFromViewpoint(
  viewpoint: MapViewpoint | null,
  fallback: { center: [number, number]; zoom: number }
): { center: [number, number]; zoom: number } {
  const longitude = viewpoint?.longitude ?? fallback.center[0];
  const latitude = viewpoint?.latitude ?? fallback.center[1];
  const zoom =
    viewpoint?.zoom ??
    (viewpoint?.altitudeM != null
      ? altitudeMetersToZoom(viewpoint.altitudeM, latitude)
      : fallback.zoom);
  return { center: [longitude, latitude], zoom };
}
